import {useEffect, useState} from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wrench } from 'lucide-react';
import { orderBy } from '@/lib/collections';
import {
    currentWeekYear,
    getCityBySearch,
    getReparateurByCodePostal,
    JoursFeries,
    normalizeIban
} from "@/lib/helpers";
import useBookingUrl from "@/hooks/use-booking-url";
import Text from "@/components/forms/text";
import Select from "@/components/forms/select";
import RadioGroup from "@/components/forms/radio-group";
import PostalCodeCity from "@/components/forms/postal-code-city";
import useBrandName from '@/hooks/use-brand-name';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { getContractGarantiesOptions, isWakamContract } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';
import type { Reparateur } from '@/pages/accident/sinistre/ajout-reparateur/form-ajout-reparateur';
import type { DeclarationStepProps } from '@/pages/accident/declaration-sinistre/types';

const Reparation = (props: DeclarationStepProps) => {
    const brandName = useBrandName();
    const contract = useSelectedContract()
    const garanties = getContractGarantiesOptions(contract)
    const isWakam = isWakamContract(contract)
    const { marque, modele, immatriculation } = contract?.vehicule || {}
    const prelevement = contract?.prelevement
    const { errors, values, setValue, handleChange } = props
    const [reparateurs, setReparateurs] = useState<Reparateur[]>([])
    const [addressReparateur, setAddressReparateurs] = useState<string | null | undefined>(null)
    const [missions, setMissions] = useState<string[]>([])
    const [, setCity] = useState<unknown>([])
    const bookingUrl = useBookingUrl('rdv_garage')

    useEffect(() => {
        if(values.cpreparateur !== undefined){
            void fetchCityByPostalCode(values.cpreparateur)
            void fetchReparateurs(values.cpreparateur)
        }
    }, [values.cpreparateur])

    /* ajout l'id du reparateur dans le form */
    const changeValueReparateurVisites = (reparateur: string) => {
        setValue('reparateur', reparateur)
        if (!(!garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5', '104.1'].includes(values.catsinorigin))){
            setValue('visit', reparateur !== 'NONE' ? missions[0] : '')
        }
    }

    /* Retire les jours feries d'un tableau de date */
    const removeJoursFeries = (array: string[]) => {
        const joursFeries = JoursFeries(currentWeekYear())

        /* Retrait en place : le tableau est celui du réparateur, réutilisé ensuite */
        for (let i = array.length - 1; i >= 0; i--) {
            if (joursFeries.includes(array[i])) array.splice(i, 1)
        }
    }

    /* Function async qui va chercher les villes via le code postal */
    async function fetchCityByPostalCode(value: string){
        const cities = await getCityBySearch(value)
        setCity(cities)
    }

    /* Function async qui va chercher les reparateur via le code postal */
    async function fetchReparateurs(postalCode: string){
        const arrayReparateurs = await getReparateurByCodePostal<Reparateur[]>(postalCode)
        const reparateurs = orderBy(arrayReparateurs, ['raisonSociale'], ['asc'])
        reparateurs.push({id: 'NONE', raisonSociale: 'Autre garage', datesVisite: []})

        removeJoursFeries(reparateurs[0].datesVisite)
        setReparateurs(reparateurs)
        setMissions(reparateurs[0].datesVisite)
        setAddressReparateurs(reparateurs[0].rue1)

        changeValueReparateurVisites(reparateurs[0].id)
    }

    /* Ajout l'addresse du réparateur et les missions disponible apres la selection du reparateur */
    const getAdresseReparateur = (id: string) => {
        const item = reparateurs.find((r) => r.id === id) as Reparateur

        removeJoursFeries(item.datesVisite)

        setAddressReparateurs(item.rue1)
        setMissions(item.datesVisite)

        changeValueReparateurVisites(item.id)
    }

    return (
        <Card className="mt-6">
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-primary" />
                    Votre Réparation
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
                <Alert variant="warning">
                    <AlertDescription>
                        <p>Garage où se trouve le véhicule : <b>{marque} {modele}</b> - Immatriculé : <b>{immatriculation}</b></p>
                        <p className="mt-1">En cas de dommages garantie par votre contrat, vous avez la faculté de choisir le réparateur professionnel auquel vous souhaitez recourir</p>
                    </AlertDescription>
                </Alert>

                <PostalCodeCity
                    label={'Code postal / Ville du garage ou réparateur'}
                    cp='cpreparateur'
                    ville='reparateurAutreVille'
                    required
                    error={errors.cpreparateur}
                />

                <Select
                    required
                    name={"reparateur"}
                    error={errors.reparateur}
                    label={'Garage / Réparateur'}
                    value={values.reparateur}
                    onChange={(e) => getAdresseReparateur(e.target.value)}
                >
                    {reparateurs.map((r, i) => (
                        <option key={i} value={r.id}>{r.raisonSociale}</option>
                    ))}
                </Select>
                {bookingUrl && <p className={'mt-2 text-sm italic'}>Vous ne trouvez pas votre garage ? <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={'underline italic'}>Prenons rendez-vous</a></p>}

                {values.reparateur && values.reparateur !== 'NONE' && (
                    <Alert variant="warning">
                        <AlertDescription>Adresse : {addressReparateur}</AlertDescription>
                    </Alert>
                )}

                {values.reparateur && values.reparateur === 'NONE' && (
                    <div className="space-y-6">
                        <Text
                            name="reparateurAutreNom"
                            label={'Nom du garage'}
                            value={values.reparateurAutreNom}
                            error={errors.reparateurAutreNom}
                            onChange={handleChange}
                            normalize={v => v.toUpperCase()}
                        />
                        <Text
                            name="reparateurAutreAdresse"
                            label={'Adresse du garage'}
                            value={values.reparateurAutreAdresse}
                            error={errors.reparateurAutreAdresse}
                            onChange={handleChange}
                            normalize={v => v.toUpperCase()}
                        />
                    </div>
                )}

                {(values.reparateur && values.reparateur === 'NONE') || (!garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5', '104.1'].includes(values.catsinorigin))
                    ? null
                    : isWakam ? null : (
                        <div className="space-y-2">
                            <Select
                                name={"visit"}
                                error={errors.visit}
                                label={'Date de passage de l\'expert'}
                                value={values.visit}
                            >
                                {missions.map((m, i) => (
                                    <option key={i} value={m}>{formatDate(m, 'EEEE dd MMMM yyyy')}</option>
                                ))}
                            </Select>
                            <p className="text-destructive text-sm font-semibold">Cette date est susceptible d'être modifiée, un mail de confirmation vous sera envoyé lorsque l'expertise sera validée.</p>
                        </div>
                    )
                }

                <div className="space-y-6 pt-6 border-t">
                    <p className="font-semibold text-base text-primary">Dans le cas du remboursement du sinistre, vous préférez</p>
                    <RadioGroup
                        label={'Ne pas avoir à avancer des frais auprès du garagiste'}
                        name={'remb_garage'}
                        setValue={setValue}
                        options={[{value: 'T', label: 'Oui'}]}
                        value={values.remb_garage}
                        error={errors.remb_garage}
                        required
                    />
                    <RadioGroup
                        label={`Régler le garagiste vous-même puis être remboursé par ${brandName} ultérieurement`}
                        name={'remb_garage'}
                        setValue={setValue}
                        options={[{value: 'F', label: 'Oui'}]}
                        value={values.remb_garage}
                        error={errors.remb_garage}
                        required
                    />
                </div>

                {(values.remb_garage === 'F' && !prelevement) && (
                    <div className="space-y-6 pt-6 border-t">
                        <Text
                            type={'text'}
                            name="iban"
                            label={'IBAN'}
                            onChange={handleChange}
                            value={values.iban}
                            error={errors.iban}
                            normalize={normalizeIban}
                            required
                        />
                        <Text
                            type={'text'}
                            name="domiciliation"
                            label="Domiciliation de la banque"
                            onChange={handleChange}
                            value={values.domiciliation}
                            error={errors.domiciliation}
                            required
                            normalize={v => v.toUpperCase()}
                        />
                        <Text
                            type={'text'}
                            name="bic"
                            label="Code BIC"
                            onChange={handleChange}
                            value={values.bic}
                            error={errors.bic}
                            required
                            normalize={v => v.toUpperCase()}
                        />
                        <Alert>
                            <AlertDescription>
                                Vos informations bancaires serviront uniquement pour le remboursement de ce sinistre.
                            </AlertDescription>
                        </Alert>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default Reparation;
